import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {BasketsFacade} from '@kion/data-access/data-access-baskets';
import {BehaviorSubject, combineLatest, interval, Observable, Subject} from 'rxjs';
import {BaseCmsComponent, BaseSite, Basket, BasketEntry, CmsComponent, CmsLinkComponent} from '@kion/data-models';
import {debounce, map, take, takeUntil} from 'rxjs/operators';
import {CmsConfig} from '../../config/cms.config';
// tslint:disable-next-line:nx-enforce-module-boundaries
import {Sum} from '../../../../../lr-ui/src/lib/elements/basket-summary/basket-summary.component';
import {CmsComponentsFacade} from '@kion/data-access/data-access-cms-components';
// tslint:disable-next-line:nx-enforce-module-boundaries
import {Checkbox} from '../../../../../../shared/ui/src/lib/shared/elements/checkbox/checkbox.component';
import {CorrelationObserverService} from '@kion/ngrx-helper';
import {ActivatedRoute, Router} from '@angular/router';
// tslint:disable-next-line:nx-enforce-module-boundaries
import {ToastService} from '@kion/ui';
import {SubSink} from 'subsink';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {GoogleTagManagerService} from 'angular-google-tag-manager';
import {TranslateService} from '@ngx-translate/core';
import {BaseSitesFacade} from '@kion/data-access/data-access-base-sites';

@Component({
  selector: 'kion-cms-checkout-summary',
  templateUrl: './checkout-summary.component.html',
  styleUrls: ['./checkout-summary.component.scss']
})
export class CheckoutSummaryComponent implements OnInit, OnDestroy {

  @Input() component: BaseCmsComponent;

  basket$: Observable<Basket>;
  pricing$: Observable<Sum>;
  error$: Observable<any>;
  isMultiVendor$: Observable<boolean> = this.baseSitesFacade.all$.pipe(
    map(sites => sites.find(site => site.uid === this.cmsConfig.baseSite)),
    map(site => site.multiVendor)
  );

  basketSubject$: Subject<Basket> = new Subject<Basket>();

  checkboxes$: Observable<Checkbox[]>;
  multiVendorCheckboxes$: Observable<Checkbox[]>;
  requiredBoxesChecked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  multiVendorRequiredBoxesChecked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  baseSite$: Observable<BaseSite>;

  addressLink: string;
  deliveryLink: string;
  paymentLink: string;
  backLink: string;
  dataPrivacyLink: string;
  contractPartnerDataPrivacyLink: string;

  saveOrder: boolean;

  form: FormGroup;

  order$: Observable<Basket>;

  private subSink: SubSink;

  constructor(
    private basketsFacade: BasketsFacade,
    private cmsConfig: CmsConfig,
    private cmsComponentsFacade: CmsComponentsFacade,
    private observer: CorrelationObserverService,
    private router: Router,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private gtmService: GoogleTagManagerService,
    private translateService: TranslateService,
    private baseSitesFacade: BaseSitesFacade,
    formBuilder: FormBuilder
  ) {
    const baseRoute = this.cmsConfig.routes?.checkoutBaseRoute;
    this.backLink = '/' + baseRoute + '/' + this.cmsConfig.routes?.checkoutRoutes?.payment;
    this.addressLink = '/' + baseRoute + '/' + this.cmsConfig.routes?.checkoutRoutes?.address;
    this.deliveryLink = '/' + baseRoute + '/' + this.cmsConfig.routes?.checkoutRoutes?.delivery;
    this.paymentLink = this.backLink;

    this.form = formBuilder.group(
      {
        additionalInfo: new FormControl(null, [Validators.maxLength(255)])
      }
    );

    this.subSink = new SubSink();
  }

  get canProceed(): boolean {
    let baseSiteVal: BaseSite = null;
    this.baseSite$.pipe(take(1)).subscribe(
      baseSite => baseSiteVal = baseSite
    );
    if (baseSiteVal.multiVendor) {
      return this.requiredBoxesChecked$.value && this.multiVendorRequiredBoxesChecked$.value
    }
    return this.requiredBoxesChecked$.value
  }

  get additionalInfo(): AbstractControl {
    return this.form.get("additionalInfo")
  }

  ngOnInit(): void {
    if (this.route.snapshot.queryParams.error) {
      this.showError(this.route.snapshot.queryParams.error);
    }
    this.order$ = this.basketsFacade.order$;
    this.loadCheckboxes();
    this.observer.subscribeToAction(
      this.basketsFacade.createOrFindBasket(),
      () => {
        this.handleBasket()
      },
      () => {
        //TODO: handle error
      }
    )
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  requiredSelected(allSelected: boolean): void {
    this.requiredBoxesChecked$.next(allSelected);
  }

  multiVendorRequiredSelected(allSelected: boolean) {
    this.multiVendorRequiredBoxesChecked$.next(allSelected);
  }

  placeOrder(): void {
    this.saveOrder = true;
    this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
      (basket: Basket) => {
        this.subSink.sink = this.observer.subscribeToAction(
          this.basketsFacade.placeOrder(basket.id),
          () => {
            window.localStorage.setItem("orderCartId", basket.id);
            this.subSink.sink = this.basketsFacade.redirectUrl$.pipe(take(1)).subscribe(
              url => {
                if (url) {
                  window.location.href = url;
                  return;
                }
                this.saveOrder = false;
                this.addConversion();
                this.routeToOrderConfirmation()
              }
            )
          },
          () => this.onSetOrderError()
        );
      }
    )
  }

  private handleBasket() {
    this.basket$ = this.basketsFacade.all$.pipe(
      map(
        (baskets: Basket[]) => baskets.find(
          (basket: Basket) => basket.id === window.localStorage.getItem('basketId')
        )
      )
    );
    this.pricing$ = this.basket$.pipe(
      map(
        (basket: Basket) => {
          if (basket) {
            return {
              subTotal: basket.subTotal?.formattedValue,
              total: basket.totalPriceWithTax?.formattedValue,
              delivery: basket.deliveryCost?.formattedValue,
              vat: basket.totalTax?.formattedValue,
              discount: basket.totalDiscounts?.value !== 0 ? basket.totalDiscounts?.formattedValue : null,
              subTotalWithDiscounts: basket.totalDiscounts?.value !== 0 ? basket.subTotalWithDiscounts?.formattedValue : null
            }
          }
        }
      )
    );
    this.error$ = this.basketsFacade.error$;
    const transactionId = this.route.snapshot.queryParams.transactionId;
    if (transactionId) {
      this.subSink.sink = this.observer.subscribeToAction(
        this.basketsFacade.finalizePayment(window.localStorage.getItem("orderCartId"), transactionId),
        () => {
          this.addConversion();
          this.routeToOrderConfirmation()
        },
        () => {
          this.showError('finalizePayment')
        }
      )
    }
    this.subSink.sink = this.basketSubject$.pipe(take(1)).subscribe(
      (basket: Basket) => {
        if (basket.userReference) {
          this.form.get('additionalInfo').patchValue(basket.userReference);
        }
      }
    );
    this.subSink.sink = this.basket$.pipe(takeUntil(this.basketSubject$)).subscribe(
      basket => {
        if (basket) {
          this.basketSubject$.next(basket);
        }
      }
    );
    this.subSink.sink = this.form.get('additionalInfo').valueChanges
      .pipe(debounce(() => interval(500)))
      .subscribe(
        note => {
          this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
            (basket: Basket) => {
              if (basket) {
                if (note !== basket.userReference) {
                  this.basketsFacade.addUserReference(basket.id, note)
                }
              }
            }
          )
        }
      );

    this.subSink.sink = this.basket$.subscribe(
      basket => {
        if (basket) {
          const contractPartnerName = basket.entries[0].fulfillmentBy?.registeredName;
          const contractPartnerUid = basket.entries[0].fulfillmentBy?.uid;
          this.contractPartnerDataPrivacyLink = `<a href="${this.cmsConfig.basePath}${this.cmsConfig.routes.partnerBaseRoute}/${contractPartnerUid}/${this.cmsConfig.routes.privacyPolicyBaseRoute}" target="_blank">${this.translateService.instant('checkout.steps.summary.checkbox.link.dataPrivacy.contractPartner', {contractPartnerName: contractPartnerName})}</a>`;
          this.dataPrivacyLink = `<a href="${this.cmsConfig.basePath}${this.cmsConfig.routes.privacyPolicyBaseRoute}" target="_blank">${this.translateService.instant('checkout.steps.summary.checkbox.link.dataPrivacy.lmh')}</a>`;
        }
      }
    );
    this.multiVendorCheckboxes$ = combineLatest(
      [
        this.baseSitesFacade.all$,
        this.basket$
      ]
    ).pipe(
      map(
        ([baseSites, basket]) => (
          {
            baseSite: baseSites.find(
              (baseSite: BaseSite) => baseSite.id === this.cmsConfig.baseSite
            ),
            basket: basket
          }
        )
      ),
      map(
        ({baseSite, basket}) => {
          const contractPartnerUid = basket.entries[0].fulfillmentBy?.uid;
          const contractPartnerName =  basket.entries[0].fulfillmentBy?.registeredName;
          if (baseSite.multiVendor) {
            const contractPartnerAgbLink = `<a href="${this.cmsConfig.basePath}${this.cmsConfig.routes.partnerBaseRoute}/${contractPartnerUid}/${this.cmsConfig.routes.termsBaseRoute}" target="_blank">${this.translateService.instant('checkout.steps.summary.checkbox.link.terms.contractPartner')}</a>`;
            return [
              {
                title: this.translateService.instant("checkout.steps.summary.checkbox.contractPartner", {contractPartnerAgbLink: contractPartnerAgbLink, contractPartnerName}),
                isLink: false,
                required: true,
                checked: false,
                titleAsHtml: true,
              }
            ]
          }
        }
      )
    );
  }

  private loadCheckboxes(): void {
    const components = this.component.checkboxes?.split(' ');
    if (typeof components === 'undefined' || components?.length === 0) {
      this.requiredBoxesChecked$.next(true)
    }
    this.cmsComponentsFacade.loadByIds(components, components?.join('_'));
    this.checkboxes$ = this.cmsComponentsFacade.all$.pipe(
      map(
        (cmsComponents: CmsComponent[]) => cmsComponents.filter(
          (cmsComponent: CmsComponent) => components?.includes(cmsComponent.uid)
        ).sort(
          (a, b) => {
            const indexA = components.indexOf(a.uid);
            const indexB = components.indexOf(b.uid);
            return indexA < indexB ? -1 : 1;
          }
        ) as CmsLinkComponent[]
      ),
      map(
        (c) => c.map(
          (component: any) => {
            return {
              title: component.linkName,
              isLink: component.suppressLink === "false",
              required: component.required === "true",
              checked: false,
              linkRef: component
            }
          }
        )
      )
    );
    this.baseSite$ = this.baseSitesFacade.all$.pipe(
      map(
        (baseSites: BaseSite[]) => baseSites.find(
          (baseSite: BaseSite) => baseSite.id === this.cmsConfig.baseSite
        )
      )
    );
  }

  private onSetOrderError() {
    this.saveOrder = false;
    this.subSink.sink = this.error$.pipe(take(1)).subscribe(
      (response) => {
        const errors = response.error.errors;
        if (errors) {
          errors.forEach(error => {
            let errorCode = error.errorCode;

            if (!errorCode && error.type === 'KionConcardisServiceError') {
              errorCode = 'KionConcardisServiceError';
            } else if (errorCode !== '16002') {
              errorCode = 'generalOrderError';
            }

            this.showError(errorCode);
          });
        }
      }
    );
  }

  private showError(errorCode: string) {
    this.toastService.show(
      {
        type: 'error',
        text: this.translateService.instant('toast.checkout.summary.error.' + errorCode)
      }
    )
  }

  private addConversion(): void {
    this.getTransaction();
  }

  private routeToOrderConfirmation(): void {
    this.subSink.sink = this.order$.pipe(take(1)).subscribe(
      (basket: Basket) => {
        this.router.navigate([this.cmsConfig.routes.checkoutBaseRoute, this.cmsConfig.routes.checkoutRoutes.confirmation, basket.code]).then(() => {
        });
      }
    )
  }

  private getTransaction(): void {
    this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
      (basket: Basket) => {
        const eCommerce = {
          'event': 'transaction',
          'ecommerce': {
            'purchase': {
              'actionField': {
                'id': basket.code,
                'affiliation': 'Linde Shop ' + this.cmsConfig.isoCode,
                'revenue': basket.totalPriceWithTax?.value || 0,
                'tax': basket.totalTax?.value || 0,
                'shipping': basket.deliveryCost?.value || 0,
                'coupon': basket.totalDiscounts?.value || 0
              },
              'products': basket.entries.map(
                (entry: BasketEntry) => {
                  return {
                    'name': entry.product.name,
                    'id': entry.product.code,
                    'price': entry.product.price.value,
                    'brand': 'Linde',
                    'category': 'Products',
                    'variant': '',
                    'quantity': entry.quantity,
                    'coupon': ''
                  }
                }
              )
            }
          }
        };
        this.gtmService.pushTag(eCommerce).then(() => {
        });
      }
    );
  }


}
